/**
 * serviceDraft — what a servicing or conversion record has to be before it can be one.
 *
 * SERVICE IS THE ONE PLURAL PREP ITEM. Registration is 1:1, roadworthiness is at most one, the
 * trackers are a fixed pair — but a car can go through the workshop several times before it is
 * handed over, and `get_preparation_status` clears its gate on the FIRST row of type `servicing`
 * or `conversion`. So this is the only vehicle block that is a list with an add row rather than a
 * single group, and the two types that clear the gate are the two the picker offers.
 *
 * THE MONEY IS THE TRANSACTION'S, NOT THE RECORD'S. `vehicle_maintenances` has no amount column —
 * it carries `transaction_id`, NOT NULL, and the amount an admin sees comes off the linked DEBIT.
 * That is why the transaction row is required on a new record and why there is no amount field to
 * type into: the number is already in the bank.
 *
 * PURE, AND REACT-FREE — vitest runs node-env over `.ts` only.
 */

import { format } from 'date-fns';

import { businessDay, formatCurrency } from '@/lib/format';

/** The two `maintenance_type` members `get_preparation_status` counts. */
export const SERVICE_TYPES = ['servicing', 'conversion'] as const;
export type ServiceType = (typeof SERVICE_TYPES)[number];

export const SERVICE_TYPE_LABELS: Record<string, string> = {
    servicing: 'Servicing',
    conversion: 'Conversion',
    refinement: 'Refinement',
    warranty: 'Warranty',
    insurance: 'Insurance work',
    refurbishment: 'Refurbishment',
};

/** A stored type, in the words the canvas uses. Unknown members print themselves. */
export function serviceTypeLabel(type: string | null | undefined): string {
    if (!type) return 'Work';
    return SERVICE_TYPE_LABELS[type] ?? type;
}

/** The fields one row of the list holds, as strings, exactly as the inputs hold them. */
export interface ServiceDraft {
    type: string;
    description: string;
    startedAt: string;
    endedAt: string;
    /** `transactions.id` as a string — '' when nothing is linked. */
    transactionId: string;
}

/**
 * The refusal for a record that cannot be saved, or null.
 *
 * THE TRANSACTION IS REQUIRED, and that is the column speaking: `transaction_id` is NOT NULL, so a
 * row without one is refused by Postgres with a sentence about a constraint. The sheet this
 * replaces asked for it on the way in and then sent whatever the picker held on every edit too, so
 * an admin who cleared it got the constraint rather than an answer.
 */
export function validateServiceDraft(draft: ServiceDraft): string | null {
    if (draft.description.trim() === '') return 'Describe the work that was done.';
    if (!draft.startedAt.trim()) return 'Enter the date the work started.';
    if (draft.endedAt.trim() && draft.endedAt < draft.startedAt) {
        return 'The work cannot have finished before it started.';
    }
    if (draft.transactionId.trim() === '') {
        return 'Link the payment for this work — the record cannot be saved without one.';
    }
    return null;
}

/**
 * One stored record, as the collapsed summary row reads: `Servicing · completed Jul 3, 2026`.
 *
 * THE COMPLETION DATE IS A CALENDAR DAY. `vehicle_maintenances.ended_at` is a TIMESTAMPTZ the form
 * fills from a date input, so the app reads back Accra midnight; `new Date(…)` rendered through
 * date-fns then dated the work in the VIEWER's zone, a day early west of Greenwich. `businessDay`
 * lands it on the Ghana calendar day, and an unreadable value now reads `in progress` rather than
 * `completed Invalid Date`.
 */
export function serviceSummary(row: {
    type: string | null;
    description: string | null;
    ended_at: string | null;
    amount: number | null;
}): string {
    const completed = businessDay(row.ended_at);
    return [
        row.description?.trim() || serviceTypeLabel(row.type),
        completed ? `completed ${format(completed, 'MMM d, yyyy')}` : 'in progress',
        row.amount == null ? null : formatCurrency(row.amount),
    ]
        .filter(Boolean)
        .join(' · ');
}
